import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  UserSearch,
  RefreshCw,
  Trash2,
  Plus,
  Loader2,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  nome: "",
  cognome: "",
  cellulare: "",
  email: "",
  qualifica: "",
  zone_di_ricerca: "",
  note: "",
};

export default function RecruitingCandidatiAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
      const res = await fetch(`${API}/api/admin/recruiting-candidati${qs}`, {
        headers: authHeaders,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Errore caricamento candidati");
      }
      const json = await res.json();
      setItems(json.data || []);
    } catch (e) {
      toast.error(e.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const openCreate = () => {
    setForm(EMPTY);
    setFile(null);
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!form.nome.trim()) {
      toast.error("Nome obbligatorio");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (file) fd.append("file", file);
      const res = await fetch(`${API}/api/admin/recruiting-candidati`, {
        method: "POST",
        headers: authHeaders,
        body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Creazione fallita");
      toast.success("Candidato creato");
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Disattivare questo candidato?")) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${API}/api/admin/recruiting-candidati/${id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Eliminazione fallita");
      toast.success("Candidato eliminato");
      await load();
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <UserSearch className="w-5 h-5 text-violet-600" />
            Candidati recruiting
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Pool candidati mostrati nel modulo Personale delle strutture.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input
            className="w-48 h-9"
            placeholder="Cerca..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button size="sm" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" />
            Nuovo candidato
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((c) => (
          <Card key={c.id}>
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium">
                  {c.nome} {c.cognome}
                </p>
                <p className="text-sm text-slate-500 mt-0.5">
                  {[c.qualifica, c.email, c.cellulare].filter(Boolean).join(" · ") || "—"}
                </p>
                {c.zone_di_ricerca && (
                  <p className="text-xs text-slate-400 mt-1">Zone: {c.zone_di_ricerca}</p>
                )}
                <div className="flex gap-2 mt-2">
                  {c.has_curriculum && (
                    <Badge className="bg-violet-100 text-violet-700">CV</Badge>
                  )}
                  {c.is_active === false && (
                    <Badge variant="secondary">Disattivo</Badge>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:bg-red-50"
                disabled={deletingId === c.id}
                onClick={() => handleDelete(c.id)}
              >
                {deletingId === c.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
        {!loading && items.length === 0 && (
          <Card>
            <CardContent className="p-10 text-center text-slate-500">
              Nessun candidato
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo candidato</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2 py-2">
            {[
              ["nome", "Nome *"],
              ["cognome", "Cognome"],
              ["cellulare", "Cellulare"],
              ["email", "Email"],
              ["qualifica", "Qualifica"],
              ["zone_di_ricerca", "Zone di ricerca"],
            ].map(([key, label]) => (
              <div key={key} className="space-y-1.5">
                <Label>{label}</Label>
                <Input value={form[key]} onChange={(e) => set(key, e.target.value)} />
              </div>
            ))}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Note</Label>
              <Input value={form.note} onChange={(e) => set("note", e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>CV (opzionale)</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
